import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import type { QlikApp } from "@repo/shared";
import { useTranslation } from "react-i18next";
import { Button, PageContent, PageHeader, PageShell, type DataTableState } from "@h2j-data-team/ui";
import { toast } from "@/components/shared";
import { AppTable } from "@/components/app/app-table";
import { EMPTY_APP_FILTERS, type AppFilters } from "@/components/app/app-filters";
import { BackLink } from "@/components/back-link";
import { getErrorMessage } from "@/lib/api-client";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import {
  connectionQueryOptions,
  connectionAppsQueryOptions,
  appMetadataQueryOptions,
  useRefreshAppMetadata,
  useRefreshAllAppsMetadata,
} from "@/features/qlik-connections/api";

export const Route = createFileRoute("/_authenticated/connections/$connectionId/apps")({
  staticData: { pageTitle: "breadcrumbs.connections", breadcrumb: "breadcrumbs.apps" },
  component: ApplicationsPage,
});

function ApplicationsPage() {
  const { t } = useTranslation("connections");
  const { connectionId } = useParams({ from: "/_authenticated/connections/$connectionId/apps" });
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  const [tableState, setTableState] = useState<DataTableState<AppFilters>>({
    page: 1,
    pageSize: 50,
    query: "",
    filters: EMPTY_APP_FILTERS,
    selection: [],
  });

  const connectionQuery = useQuery(connectionQueryOptions(connectionId));
  const connection = connectionQuery.data;

  const appsQuery = useQuery({
    ...connectionAppsQueryOptions(connectionId),
    enabled: !!connectionId,
  });
  const { data: apps, refetch: refetchApps, isFetching: isFetchingApps } = appsQuery;

  const metadataQueries = useQueries({
    queries: (apps ?? []).map((app: QlikApp) => ({
      ...appMetadataQueryOptions(connectionId, app.id),
      enabled: !!apps,
    })),
  });
  const metadataByAppId = useMemo(() => {
    const map = new Map<string, string | null>();
    (apps ?? []).forEach((app: QlikApp, idx) => {
      const fetchedAt = metadataQueries[idx]?.data?.fetchedAt ?? null;
      map.set(app.id, fetchedAt);
    });
    return map;
  }, [apps, metadataQueries]);

  const refreshAppMetadata = useRefreshAppMetadata();
  const refreshAllMetadata = useRefreshAllAppsMetadata();

  const failed = connectionQuery.isError || appsQuery.isError;
  const retry = () => {
    if (connectionQuery.isError) void connectionQuery.refetch();
    if (appsQuery.isError) void refetchApps();
  };

  return (
    <PageShell
      header={
        <PageHeader
          title={connection?.name || t("apps.pageTitle")}
          description={
            failed
              ? t("apps.errorDescription")
              : connection
                ? t("apps.description", { name: connection.name })
                : t("apps.loadingDescription")
          }
          back={
            <BackLink
              to="/connections"
              search={{ q: undefined }}
              label={t("apps.backToConnections")}
            />
          }
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                loading={refreshAllMetadata.isPending}
                onClick={() =>
                  refreshAllMetadata.mutate(connectionId, {
                    onSuccess: () => toast.success(t("apps.metadataRefreshStarted")),
                    onError: (err) =>
                      toast.error(getErrorMessage(err, t("apps.metadataRefreshFailed"))),
                  })
                }
                disabled={!apps || apps.length === 0}
              >
                <RefreshCw />
                {t("apps.refreshAll")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                loading={isFetchingApps}
                onClick={() => void refetchApps()}
              >
                <RefreshCw />
                {t("apps.refresh")}
              </Button>
            </>
          }
        />
      }
    >
      <PageContent fill>
        <AppTable
          label={t("apps.pageTitle")}
          apps={apps ?? []}
          connectionId={connectionId}
          state={tableState}
          onStateChange={setTableState}
          loading={connectionQuery.isPending || appsQuery.isPending}
          error={failed ? { title: t("apps.loadFailed") } : null}
          onRetry={retry}
          searchRef={searchRef}
          showCache
          cacheByAppId={metadataByAppId}
          refreshing={refreshAppMetadata.isPending}
          refreshingAppId={
            refreshAppMetadata.isPending ? refreshAppMetadata.variables?.appId : null
          }
          emptyAction={
            <Button asChild variant="outline">
              <Link to="/connections" search={{ q: undefined }}>
                {t("apps.backToConnections")}
              </Link>
            </Button>
          }
          onRefreshMetadata={(appId) =>
            refreshAppMetadata.mutate(
              { connectionId, appId },
              {
                onSuccess: () =>
                  toast.success(
                    t("apps.metadataUpdated", {
                      name: apps?.find((a: QlikApp) => a.id === appId)?.name ?? "",
                    }),
                  ),
                onError: (err) => toast.error(getErrorMessage(err, t("apps.metadataUpdateFailed"))),
              },
            )
          }
        />
      </PageContent>
    </PageShell>
  );
}
